import { useEffect, useState } from 'react'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { InstagramReelPreview } from 'components/Previews/InstagramReelPreview/InstagramReelPreview'
import { trackEventInHeap, HeapEventType } from 'utils/heapTracking'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useUpdateBrandKit } from 'hooks/queries/brandKits/useUpdateBrandKit'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useQueryClient } from 'react-query'
import { useCreditUsage } from 'hooks/useCreditUsage'
import Styles from './BrandVisualStyleAnimatedImages.module.scss'

const VIDEO_PREVIEW_URL =
  'https://res.cloudinary.com/almanac/video/upload/v1767834736/blaze_assets/content_preferences/smiling_model_ni1ccz.mp4'

export interface BrandVisualStyleAnimatedImagesProps {
  onNext: (optedIn: boolean) => void
  onBack: () => void
  brandKitId: number
}

export const BrandVisualStyleAnimatedImages = ({ onNext, onBack, brandKitId }: BrandVisualStyleAnimatedImagesProps) => {
  const { data: brandKitData } = useBrandKit(brandKitId)
  const { mutateAsync: updateBrandKitMutation } = useUpdateBrandKit({ id: brandKitId })
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const [isSaving, setIsSaving] = useState(false)
  const { usageCosts } = useCreditUsage()
  const animatedImagesCost = usageCosts?.video_credits_cost || 0

  useEffect(() => {
    trackEventInHeap(HeapEventType.Onboarding, {
      action: 'view-animated-images-step',
    })
  }, [])

  const handleOptIn = async (optedIn: boolean) => {
    setIsSaving(true)

    const preferences = optedIn
      ? {
          video_sources: ['animated_image' as const],
          video_source_animated_image_weight: 100,
          video_source_brand_kit_stock_weight: 0,
        }
      : {
          video_sources: ['brand_kit_stock' as const],
          video_source_animated_image_weight: 0,
          video_source_brand_kit_stock_weight: 100,
        }

    trackEventInHeap(HeapEventType.Onboarding, {
      action: 'complete-animated-images-step',
      animated_images_opt_in: optedIn,
    })

    try {
      await updateBrandKitMutation(
        {
          content_preferences: {
            ...brandKitData?.brand_kit?.content_preferences,
            ...preferences,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', 'detail', brandKitId])
          },
        },
      )
      onNext(optedIn)
    } finally {
      setIsSaving(false)
    }
  }

  const handleBack = () => {
    trackEventInHeap(HeapEventType.Onboarding, {
      action: 'back-from-animated-images-step',
    })
    onBack()
  }

  return (
    <div className={Styles.selectImageStep}>
      <div className={Styles.leftSide}>
        <div className={Styles.title}>
          <Heading level={1} color="var(--dark-80)">
            Transform your photos into engaging AI videos
          </Heading>
          <Text color="var(--dark-40)" variant="primary">
            Blaze automatically transforms your Brand Kit photos into professional videos using AI
          </Text>
        </div>

        <div className={Styles.content}>
          <div className={Styles.featureBox}>
            <div className={Styles.featureItem}>
              <div className={Styles.featureContent}>
                <Heading level={5} color="var(--dark-90)">
                  📹 More content, zero effort
                </Heading>
                <Text variant="secondary" color="var(--dark-40)">
                  Blaze picks your best photos and turns them into videos automatically
                </Text>
              </div>
            </div>

            <div className={Styles.featureItem}>
              <div className={Styles.featureContent}>
                <Heading level={5} color="var(--dark-90)">
                  🎯 Scroll-stopping quality
                </Heading>
                <Text variant="secondary" color="var(--dark-40)">
                  Professional camera movements, effects, and music in every 8-second video
                </Text>
              </div>
            </div>

            <div className={Styles.featureItem}>
              <div className={Styles.featureContent}>
                <Heading level={5} color="var(--dark-90)">
                  ✅ Always ready to post
                </Heading>
                <Text variant="secondary" color="var(--dark-40)">
                  Videos appear in your calendar—review, edit, or publish instantly
                </Text>
              </div>
            </div>

            <div className={Styles.optInButtonContainer}>
              <Button variant="primary" size="xl" onPress={() => handleOptIn(true)} isDisabled={isSaving}>
                Yes, turn my images into AI videos
              </Button>
              <Text variant="label" color="var(--dark-40)">
                AI videos use {animatedImagesCost} credits each. Add credits on the next screen.
              </Text>
            </div>
          </div>

          <div className={Styles.animatedImagesActions}>
            <Button variant="secondary" size="lg" onPress={() => handleOptIn(false)} isDisabled={isSaving}>
              Continue without AI videos
            </Button>
            <Text variant="label" color="var(--dark-40)">
              Not sure? You can always disable AI videos later in settings.
            </Text>
          </div>
        </div>

        <div className={Styles.footer}>
          <Button variant="tertiary" size="lg" onPress={handleBack} aria-label="Back" isDisabled={isSaving}>
            Back
          </Button>
        </div>
      </div>

      <div className={Styles.transformedImageContainer}>
        <div className={Styles.reelPreviewContainer}>
          <InstagramReelPreview
            mediaObjects={[
              {
                id: 1,
                source_url: VIDEO_PREVIEW_URL,
                thumbnail_url: VIDEO_PREVIEW_URL,
                mime_type: 'video/mp4',
                position: 1,
              },
            ]}
            content="Great style gets better with time. Our loyalty..."
            showVideoControls={false}
            isActive
          />
        </div>
      </div>
    </div>
  )
}
